/**
 * Results gallery (005 T009) — thumbnails + refs + progress. Virtualized window
 * so hundreds of items scroll without UI freezes (008 SC-003, T020).
 */
import { useEffect, useMemo, useState } from "react";
import { EmptyState } from "../ui/EmptyState.js";
import { WorkingTimer } from "../ui/WorkingTimer.js";
import type { OutputRecord } from "@genz/platform";

export interface GalleryProps {
  outputs: OutputRecord[];
  progress: { completed: number; total: number };
  rowHeight?: number;
  viewportHeight?: number;
  /**
   * WS-H2 (AUD-3 F-15 / LB-2): fetch the bytes of an output whose `bytes` were SPILLED after
   * the auto-save wrote them to disk. Desktop only; omit it (web) and a spilled output keeps
   * today's neutral placeholder tile rather than a broken image.
   */
  resolveBytes?: (o: OutputRecord) => Promise<Uint8Array | null>;
  /** The run's clock: counting until `endedAt`, then what the run took. */
  run?: { startedAt: number; endedAt?: number };
}

export function Gallery({
  outputs,
  progress,
  rowHeight = 96,
  viewportHeight = 480,
  resolveBytes,
  run,
}: GalleryProps) {
  const [scrollTop, setScrollTop] = useState(0);
  const overscan = 4;

  const { start, end, total } = useMemo(() => {
    const first = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
    const visible = Math.ceil(viewportHeight / rowHeight) + overscan * 2;
    return { start: first, end: Math.min(outputs.length, first + visible), total: outputs.length };
  }, [scrollTop, rowHeight, viewportHeight, outputs.length]);

  const pct = progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0;

  return (
    <section className="gallery" data-testid="gallery">
      <div className="gallery__progress">
        <span>Created {progress.completed}/{progress.total}</span>
        <div className="progress" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
          <div className="progress__bar" style={{ width: `${pct}%` }} />
        </div>
        <span>{pct}%</span>
        {run && progress.total > 0 && (
          <span data-testid="gallery-run-time">
            {run.endedAt === undefined
              ? <WorkingTimer startedAt={run.startedAt} />
              : <WorkingTimer startedAt={run.startedAt} endedAt={run.endedAt} state="done" label="Took" />}
          </span>
        )}
      </div>
      {outputs.length === 0 ? (
        <EmptyState
          icon="grid"
          title="No mock-ups yet"
          hint="Upload designs and start a batch to see results appear here."
        />
      ) : (
        <div
          className="gallery__viewport"
          style={{ height: viewportHeight, overflowY: "auto", position: "relative" }}
          onScroll={(e) => setScrollTop((e.target as HTMLDivElement).scrollTop)}
          data-testid="gallery-viewport"
        >
          <div style={{ height: total * rowHeight, position: "relative" }}>
            {outputs.slice(start, end).map((o, i) => {
              const idx = start + i;
              return (
                <div
                  key={o.id}
                  className="gallery__row"
                  style={{ position: "absolute", top: idx * rowHeight, height: rowHeight, left: 0, right: 0, display: "flex", gap: 12 }}
                >
                  <Thumb output={o} size={rowHeight - 16} resolveBytes={resolveBytes} />
                  <div className="gallery__meta">
                    <div className="gallery__slug">{o.designSlug} · {o.view}</div>
                    <code className="gallery__ref">{o.localRef}</code>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

/** PNG / JPEG / WebP magic-number sniff — distinguishes real engine output from
 *  the mock's placeholder bytes (which aren't a decodable image). */
const IMAGE_MIME: Record<string, string> = { jpg: "image/jpeg", png: "image/png", webp: "image/webp" };

function imageMime(bytes?: Uint8Array): string | undefined {
  if (!bytes || bytes.length < 12) return undefined;
  // PNG: 89 50 4E 47
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return "image/png";
  // JPEG: FF D8 FF
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return "image/jpeg";
  // WebP: "RIFF"...."WEBP"
  if (
    bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 &&
    bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50
  ) {
    return "image/webp";
  }
  return undefined;
}

/** Longest edge of a gallery thumbnail. Rows are ~80px; the previous `<img>` pointed at the
 *  FULL 2000×2000 output (AUD-3 §4), so ~14 four-megapixel decodes lived at any scroll
 *  position. 256 covers 2× DPR at the largest row height we render. */
export const THUMB_MAX_PX = 256;

/** A downscaled object URL for `bytes`, or the full-resolution one when the browser has no
 *  `createImageBitmap`/`OffscreenCanvas` (jsdom, older webviews) or the decode fails. */
async function thumbObjectUrl(bytes: Uint8Array, mime: string | undefined): Promise<string> {
  const full = () => URL.createObjectURL(new Blob([bytes as BlobPart], { type: mime }));
  const cib = (globalThis as { createImageBitmap?: typeof createImageBitmap }).createImageBitmap;
  const OC = (globalThis as { OffscreenCanvas?: typeof OffscreenCanvas }).OffscreenCanvas;
  if (!cib || !OC) return full();
  try {
    const bitmap = await cib(new Blob([bytes as BlobPart], { type: mime }), {
      resizeWidth: THUMB_MAX_PX, resizeHeight: THUMB_MAX_PX, resizeQuality: "medium",
    });
    const canvas = new OC(bitmap.width, bitmap.height);
    const ctx = canvas.getContext("2d");
    if (!ctx) { bitmap.close(); return full(); }
    (ctx as unknown as CanvasRenderingContext2D).drawImage(bitmap, 0, 0);
    bitmap.close();
    return URL.createObjectURL(await canvas.convertToBlob({ type: "image/png" }));
  } catch {
    // A decode failure must never blank the gallery — fall back to the bytes we already have.
    return full();
  }
}

function Thumb({
  output,
  size,
  resolveBytes,
}: {
  output: OutputRecord;
  size: number;
  resolveBytes?: (o: OutputRecord) => Promise<Uint8Array | null>;
}) {
  // Real engine output carries decodable image bytes → render an <img> via an
  // object URL (revoked on unmount/replace). Mock/byteless outputs fall back to
  // the deterministic content-hash swatch.
  //
  // WS-H2: an output whose bytes were SPILLED after the auto-save has no `bytes` but does have
  // a `savedRoot` — re-read it through `resolveBytes` rather than showing a placeholder for a
  // file that is sitting on disk. A null result keeps the existing neutral tile.
  const [spilled, setSpilled] = useState<Uint8Array>();
  const bytes = output.bytes ?? spilled;
  const mime = imageMime(bytes) ?? IMAGE_MIME[output.format];
  const isImage = !!bytes && bytes.length > 0 && !!imageMime(bytes);
  const [url, setUrl] = useState<string>();

  const needsResolve =
    (!output.bytes || output.bytes.length === 0) && !!output.savedRoot && !!resolveBytes;
  useEffect(() => {
    if (!needsResolve) {
      setSpilled(undefined);
      return;
    }
    let cancelled = false;
    void resolveBytes!(output)
      .then((b) => {
        if (!cancelled && b && b.length > 0) setSpilled(b);
      })
      .catch(() => {
        // A failed re-read is not something the operator can act on HERE — the export path
        // reports it as a `missing` row naming the path. The tile stays neutral.
      });
    return () => {
      cancelled = true;
    };
    // `output` and `resolveBytes` are stable for a given row; re-running on identity would
    // re-read the file on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsResolve, output.id, output.savedRoot]);

  useEffect(() => {
    if (!isImage || typeof URL === "undefined" || !bytes) {
      setUrl(undefined);
      return;
    }
    // Async now (the downscale decodes off the main thread), so the cleanup has to cope with
    // a URL that lands AFTER the effect was torn down — revoke it there instead of leaking.
    let cancelled = false;
    let made: string | undefined;
    void thumbObjectUrl(bytes, mime).then((u) => {
      if (cancelled) { URL.revokeObjectURL(u); return; }
      made = u;
      setUrl(u);
    });
    return () => {
      cancelled = true;
      if (made) URL.revokeObjectURL(made);
    };
  }, [bytes, isImage, mime]);

  if (isImage && url) {
    return (
      <img
        className="gallery__thumb"
        src={url}
        alt={`${output.designSlug} · ${output.view}`}
        width={size}
        height={size}
        style={{ width: size, height: size, objectFit: "cover", borderRadius: 4 }}
        data-testid="gallery-thumb-image"
      />
    );
  }

  // Neutral Stage Rule: a byteless output shows a neutral gray tile with a
  // monochrome initial — never a color (the stage must stay neutral so operators
  // judge true artwork color, and a random hash color is an AI-slop tell).
  const initial = (output.designSlug || "?").trim().charAt(0).toUpperCase() || "?";
  return (
    <div
      className="gallery__thumb gallery__thumb--placeholder"
      style={{ width: size, height: size, borderRadius: 4 }}
      title={output.designSlug || output.contentHash}
      data-testid="gallery-thumb"
    >
      <span className="gallery__thumb-mono" aria-hidden="true">{initial}</span>
    </div>
  );
}
